<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import CanvasGestureGuide from './CanvasGestureGuide.vue'
import ViewerIcon from './ViewerIcon.vue'

const props = defineProps({
  open: Boolean,
  steps: {
    type: Array,
    default: () => []
  },
  root: {
    type: Object,
    default: null
  },
  softwareRotated: Boolean
})

const emit = defineEmits(['close', 'step-change'])

const index = ref(0)
const targetRect = ref(null)
const cardStyle = ref({})
const arrowStyle = ref({})
const placement = ref('bottom')
const card = ref(null)
let targetObserver = null
let observedTarget = null
let scheduledFrame = 0
let navigationDirection = 1
let skipNextIndexWatch = false

const step = computed(() => props.steps[index.value] || null)
const isLast = computed(() => index.value >= props.steps.length - 1)
const progressText = computed(() => `${index.value + 1} / ${props.steps.length}`)
const teleportTarget = computed(() => {
  // Re-evaluate when the tour opens: the viewer may have entered fullscreen
  // since this component was first mounted.
  if (!props.open) return 'body'
  if (props.softwareRotated && props.root) return props.root
  const fullscreen = document.fullscreenElement || document.webkitFullscreenElement || null
  return fullscreen === props.root ? props.root : 'body'
})
const maskStyle = computed(() => {
  const rect = targetRect.value
  if (!rect) return { display: 'none' }
  return {
    top: `${rect.top}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    borderRadius: `${rect.radius}px`
  }
})

function safeInsets() {
  const styles = getComputedStyle(document.documentElement)
  const read = name => Number.parseFloat(styles.getPropertyValue(name)) || 0
  if (props.softwareRotated) {
    return {
      top: Math.max(12, read('--safe-right')),
      right: Math.max(12, read('--safe-bottom')),
      bottom: Math.max(12, read('--safe-left')),
      left: Math.max(12, read('--safe-top'))
    }
  }
  return {
    top: Math.max(12, read('--safe-top')),
    right: Math.max(12, read('--safe-right')),
    bottom: Math.max(12, read('--safe-bottom')),
    left: Math.max(12, read('--safe-left'))
  }
}

function viewportSize() {
  if (props.softwareRotated && props.root) {
    return {
      width: props.root.clientWidth || window.innerHeight,
      height: props.root.clientHeight || window.innerWidth
    }
  }
  return { width: window.innerWidth, height: window.innerHeight }
}

function toLayoutRect(raw) {
  if (!props.softwareRotated || !props.root) return raw
  const rootRect = props.root.getBoundingClientRect()
  const rootHeight = props.root.clientHeight || window.innerWidth
  const left = raw.top - rootRect.top
  const top = rootHeight - (raw.right - rootRect.left)
  return {
    top,
    left,
    right: left + raw.height,
    bottom: top + raw.width,
    width: raw.height,
    height: raw.width
  }
}

function resolveTarget(current = step.value) {
  if (!current) return null
  if (typeof current.target === 'function') return current.target()
  return props.root?.querySelector?.(current.target) || document.querySelector(current.target)
}

function disconnectTargetObserver() {
  targetObserver?.disconnect()
  targetObserver = null
  observedTarget = null
}

function paddedRect(element) {
  const raw = toLayoutRect(element.getBoundingClientRect())
  const pad = Math.max(4, Number(step.value?.padding ?? 7))
  const { width: viewportWidth, height: viewportHeight } = viewportSize()
  const left = Math.max(4, raw.left - pad)
  const top = Math.max(4, raw.top - pad)
  const right = Math.min(viewportWidth - 4, raw.right + pad)
  const bottom = Math.min(viewportHeight - 4, raw.bottom + pad)
  return {
    top,
    left,
    right,
    bottom,
    width: Math.max(1, right - left),
    height: Math.max(1, bottom - top),
    radius: Math.max(12, Number(step.value?.radius ?? 18))
  }
}

function choosePlacement(rect, cardWidth, cardHeight, gap, insets) {
  const viewport = viewportSize()
  const available = {
    bottom: viewport.height - rect.bottom - insets.bottom,
    top: rect.top - insets.top,
    left: rect.left - insets.left,
    right: viewport.width - rect.right - insets.right
  }
  const preferred = step.value?.placement
  const needs = {
    bottom: cardHeight + gap,
    top: cardHeight + gap,
    left: cardWidth + gap,
    right: cardWidth + gap
  }
  if (preferred && available[preferred] >= needs[preferred]) return preferred
  const candidates = ['bottom', 'top', 'left', 'right']
    .filter(value => available[value] >= needs[value])
    .sort((a, b) => available[b] - available[a])
  if (candidates.length) return candidates[0]
  return Object.keys(available).sort((a, b) => available[b] - available[a])[0]
}

function positionCard() {
  if (!props.open || !card.value) return
  const insets = safeInsets()
  const viewport = viewportSize()
  const availableWidth = viewport.width - insets.left - insets.right
  const cardWidth = Math.min(step.value?.centered ? 360 : 310, availableWidth)
  card.value.style.width = `${cardWidth}px`
  const measured = toLayoutRect(card.value.getBoundingClientRect())

  if (step.value?.centered) {
    const cardHeight = Math.min(measured.height || 260, viewport.height - insets.top - insets.bottom)
    placement.value = 'center'
    arrowStyle.value = {}
    cardStyle.value = {
      top: `${Math.max(insets.top, (viewport.height - cardHeight) / 2)}px`,
      left: `${Math.max(insets.left, (viewport.width - cardWidth) / 2)}px`,
      width: `${cardWidth}px`
    }
    return
  }

  if (!targetRect.value) return
  const rect = targetRect.value
  const gap = 16
  const cardHeight = measured.height || 190
  const side = choosePlacement(rect, cardWidth, cardHeight, gap, insets)
  placement.value = side

  let top = rect.bottom + gap
  let left = rect.left + rect.width / 2 - cardWidth / 2
  if (side === 'top') top = rect.top - gap - cardHeight
  if (side === 'left') {
    top = rect.top + rect.height / 2 - cardHeight / 2
    left = rect.left - gap - cardWidth
  }
  if (side === 'right') {
    top = rect.top + rect.height / 2 - cardHeight / 2
    left = rect.right + gap
  }

  left = Math.min(viewport.width - insets.right - cardWidth, Math.max(insets.left, left))
  top = Math.min(viewport.height - insets.bottom - cardHeight, Math.max(insets.top, top))
  cardStyle.value = { top: `${top}px`, left: `${left}px`, width: `${cardWidth}px` }

  const targetCenterX = rect.left + rect.width / 2
  const targetCenterY = rect.top + rect.height / 2
  if (side === 'top' || side === 'bottom') {
    const arrowLeft = Math.min(cardWidth - 28, Math.max(28, targetCenterX - left))
    arrowStyle.value = { left: `${arrowLeft}px` }
  } else {
    const arrowTop = Math.min(cardHeight - 28, Math.max(28, targetCenterY - top))
    arrowStyle.value = { top: `${arrowTop}px` }
  }
}

function updateLayoutFor(element = observedTarget) {
  if (!props.open || !element?.isConnected) return
  const raw = element.getBoundingClientRect()
  if (raw.width < 1 || raw.height < 1 || getComputedStyle(element).visibility === 'hidden') return
  targetRect.value = paddedRect(element)
  nextTick(positionCard)
}

function scheduleLayout() {
  cancelAnimationFrame(scheduledFrame)
  scheduledFrame = requestAnimationFrame(() => {
    if (step.value?.canvasOverlay) return
    if (step.value?.centered) positionCard()
    else updateLayoutFor()
  })
}

async function measure(direction = 1) {
  if (!props.open || !props.steps.length) return
  await nextTick()

  if (step.value?.canvasOverlay) {
    disconnectTargetObserver()
    targetRect.value = null
    cardStyle.value = {}
    arrowStyle.value = {}
    placement.value = 'center'
    return
  }

  if (step.value?.centered) {
    disconnectTargetObserver()
    targetRect.value = null
    await nextTick()
    positionCard()
    return
  }

  let candidateIndex = index.value
  let element = null
  for (let checked = 0; checked < props.steps.length; checked += 1) {
    const candidate = resolveTarget(props.steps[candidateIndex])
    const raw = candidate?.getBoundingClientRect?.()
    if (candidate && raw?.width >= 1 && raw?.height >= 1 && getComputedStyle(candidate).visibility !== 'hidden') {
      element = candidate
      break
    }
    candidateIndex = (candidateIndex + direction + props.steps.length) % props.steps.length
  }
  if (!element) {
    emit('close')
    return
  }
  if (candidateIndex !== index.value) {
    skipNextIndexWatch = true
    index.value = candidateIndex
    await nextTick()
  }
  if (element !== observedTarget) {
    disconnectTargetObserver()
    observedTarget = element
  }
  updateLayoutFor(element)
  if (!targetObserver && typeof ResizeObserver !== 'undefined') {
    targetObserver = new ResizeObserver(scheduleLayout)
    targetObserver.observe(element)
  }
}

function next() {
  if (isLast.value) {
    emit('close')
    return
  }
  navigationDirection = 1
  index.value += 1
}

function handleKeydown(event) {
  if (!props.open) return
  if (event.key === 'Escape') emit('close')
  if (event.key === 'ArrowRight') next()
}

watch(() => props.open, open => {
  if (open) {
    navigationDirection = 1
    if (index.value !== 0) {
      skipNextIndexWatch = true
      index.value = 0
    }
    document.addEventListener('keydown', handleKeydown)
    window.addEventListener('resize', scheduleLayout)
    window.addEventListener('orientationchange', scheduleLayout)
    measure()
  } else {
    document.removeEventListener('keydown', handleKeydown)
    window.removeEventListener('resize', scheduleLayout)
    window.removeEventListener('orientationchange', scheduleLayout)
    disconnectTargetObserver()
    targetRect.value = null
  }
}, { immediate: true })

watch(index, value => {
  emit('step-change', value)
  if (skipNextIndexWatch) {
    skipNextIndexWatch = false
    return
  }
  measure(navigationDirection)
})

watch(() => props.softwareRotated, () => {
  if (props.open) nextTick(() => measure())
})

onBeforeUnmount(() => {
  cancelAnimationFrame(scheduledFrame)
  document.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('resize', scheduleLayout)
  window.removeEventListener('orientationchange', scheduleLayout)
  disconnectTargetObserver()
})
</script>

<template>
  <Teleport :to="teleportTarget">
    <div
      v-if="open && step"
      class="coach-tour"
      :class="{ 'is-centered-step': step.centered, 'is-canvas-step': step.canvasOverlay, 'is-software-rotated': softwareRotated }"
      :role="step.canvasOverlay ? 'region' : 'dialog'"
      :aria-modal="step.canvasOverlay ? undefined : 'true'"
      :aria-label="`帮助引导：${step.title}`"
      @click="!step.canvasOverlay && next()"
    >
      <button
        class="coach-tour__skip"
        type="button"
        aria-label="跳过帮助引导"
        @click.stop="$emit('close')"
      >跳过</button>
      <CanvasGestureGuide
        v-if="step.canvasOverlay"
        :step="step"
        :progress="progressText"
        :root="root"
        :software-rotated="softwareRotated"
        @next="next"
      />
      <template v-else>
        <div v-if="step.centered" class="coach-tour__backdrop" aria-hidden="true"></div>
        <div v-else class="coach-tour__spotlight" :style="maskStyle" aria-hidden="true"></div>
        <section
          ref="card"
          class="coach-tour__card"
          :class="[`is-${placement}`, { 'has-demo': step.demo }]"
          :style="cardStyle"
          role="document"
        >
          <i v-if="!step.centered" class="coach-tour__arrow" :style="arrowStyle" aria-hidden="true"></i>
          <header>
            <span>{{ progressText }}</span>
          </header>
          <template v-if="step.demo">
            <div class="coach-tour__title-row">
              <div v-if="step.icon" class="coach-tour__icon" aria-hidden="true"><ViewerIcon :name="step.icon" /></div>
              <div>
                <h2>{{ step.title }}</h2>
                <p>{{ step.description }}</p>
              </div>
            </div>
            <div class="coach-tour__hints" :class="`is-${step.demo}`" role="list">
              <div v-for="hint in step.hints" :key="hint.title" class="coach-tour__hint" role="listitem">
                <span class="coach-tour__motion" :class="`motion--${hint.motion}`" aria-hidden="true">
                  <ViewerIcon :name="hint.icon" />
                </span>
                <span class="coach-tour__hint-copy">
                  <strong>{{ hint.title }}</strong>
                  <small>{{ hint.text }}</small>
                </span>
              </div>
            </div>
          </template>
          <template v-else>
            <div v-if="step.icon" class="coach-tour__icon" aria-hidden="true"><ViewerIcon :name="step.icon" /></div>
            <h2>{{ step.title }}</h2>
            <p>{{ step.description }}</p>
          </template>
        </section>
        <div class="coach-tour__continue-hint" aria-hidden="true">点击任意位置继续</div>
      </template>
    </div>
  </Teleport>
</template>
